import {SpaceProto} from "../space-proto";
import ProjectSession from "../../project/project-session";
import {
    NavigationButtonPosition,
    NavigationSectionConfig,
    NavigationSectionType,
} from "../../navigation/@const";
import MainMenu_Workspace from "../../../../layout/menu/sections/workspace/workspace";
import {navigationStructureOnClick} from "../../navigation/actions/navigation-structure-on-click";
import mainMenu_workspaceViews from "../../../../layout/menu/actions/workspace-views";
import Pin from "../../pin/pin";
import {PinLocationTypes} from "../../pin/@types";
import NavigationMenuSearch from "../../../../layout/search/sections/navigation-menu/navigation-menu-search";
import {GroupedData} from "../../../data-structures/group";
import Navigation_ButtonWorkspace from "../../../../layout/navigation/buttons/workspace";
import ProjectWorkspaceProperties from "../../project/properties/workspace/workspace-properties"
import MainMenu_WorkspaceContent from "../../../../layout/menu/sections/workspace/content";

// const debug = "Workspace"
const debug = false

export default class Workspace extends SpaceProto {

    public project: ProjectSession

    constructor(project:ProjectSession) {
        super('workspace-'+project.id)

        this.project                = project

        this.updateTitleAndIcon()

        this.canHaveProjects        = true

    }

    get isWorkspace(): boolean {
        return true
    }

    get enabled(): boolean {
        return this.project.hasAccess
    }

    get inNavigationPanel(): boolean {
        if (this.project.isArchived) {
            return false
        }

        return super.inNavigationPanel
    }

    get navigationStructureConfig():NavigationSectionConfig {


        const navigationPosition : NavigationButtonPosition = (gd.user.settings.navigation.buttonPositions[this.id]===NavigationButtonPosition.MORE)?NavigationButtonPosition.MORE:NavigationButtonPosition.TOP

        return {
            id              : this.id,
            workspace       : this.project,
            icon            : this.project.iconClass || this.project.projectIcon,
            hint            : this.project.name,
            renderComponent : Navigation_ButtonWorkspace,
            type            : NavigationSectionType.WORKSPACE,
            menuComponent   : MainMenu_Workspace,
            menuContent     : MainMenu_WorkspaceContent,
            onClick         : navigationStructureOnClick,

            position        : navigationPosition,
        }
    }

    get rootTags():ProjectSession[] {

        const rootTags : ProjectSession[] = _.filter(this.project.treeChildren, (project:ProjectSession)=> {

            return project.isTag && !project.isDeleted
        })

        return rootTags

    }

    get rootProjects():ProjectSession[] {

        // we cannot just show root folders because use can have access ONLY to sub folder ie.
        // root1
        // root2 (no access)
        //     Sub 1 (has access)

        let projects : ProjectSession[] = []

        const properties    = this.project.properties as ProjectWorkspaceProperties
        const skipViews     = properties.showViewsGroup

        // we always show full tree so we can render just parents without recursive lookup
        if (gd.company.settings.projectAccess.showParentsDespiteAccess) {

            debug && console.log(debug, "showParentsDespiteAccess case")

            projects = _.filter(this.project.treeChildren, (project:ProjectSession)=> {
                return !project.isTag &&
                    (!skipViews || !project.isView) &&
                    !project.isDeleted &&
                    (project.hasAccess || project.hasAccessibleChildren)
            })
            debug && console.log(debug, "projects=", projects)
            return projects;
        }


        // do not show parents - show folder only
        debug && console.log(debug, "NOT showParentsDespiteAccess case")

        // look for first accessible project down the tree for each treeChildren and add it as root project
        const recursiveLookForRootProjects = (projectSession: ProjectSession) => {

            _.map(projectSession.treeChildren, (project:ProjectSession)=> {

                // never show deleted
                if (project.isDeleted) {
                    return
                }

                // tag has no children if no access never return
                // view - same root views we dont show here, if no access dont show at all
                if (
                    (
                        project.isTag
                        || (skipViews && project.isView)
                    )
                    &&
                    (
                        !project.hasAccess // if now access never show tag
                        || project.isWorkspaceRoot  // skip because we show workspace tags separately
                    )
                ) {
                    return
                }

                // if have access -> show else check maybe we have access to something within it's children
                if (project.hasAccess) {
                    projects.push(project)
                } else {
                    recursiveLookForRootProjects(project)
                }
            })
        }

        recursiveLookForRootProjects(this.project)

        debug && console.log(debug, "projects=", projects)

        return projects
    }

    get resources() {
        return _.filter(this.rootProjects, (project: ProjectSession)=>{
            return !project.isSection && (!project.isView || !(this.project.properties as ProjectWorkspaceProperties).showViewsGroup)
        })
    }

    get sections():ProjectSession[] {
        return _(this.project.treeChildren)
            .filter((projectSession: ProjectSession) => projectSession.isSection)
            .orderBy((projectSession: ProjectSession) => projectSession.sortPosition)
            .value()
    }

    get menuResourcesCounter() {
        // make sure we do not show root projects which are starred
        const starredProjects : ID[] = gd.session.starredItems.getProjectsIds() || []
        const workspaceRootProjects = _.filter(this.rootProjects, (project:ProjectSession)=>{
            return !starredProjects.includes(project.id) && !project.isSection
        })
        return workspaceRootProjects.length
    }

    // is required on session reset to link to new session project model
    updateProject(project: ProjectSession) {
        this.project = project
    }

    async search(keyword: string): Promise<GroupedData> {

        const workspaceViews = mainMenu_workspaceViews(this.project)

        const wsViews: ProjectSession[] = []

        _.map(workspaceViews, (projectId:ID) => {
            const project: ProjectSession | null = gd.session.projects.get(projectId)
            if(project) wsViews.push(project)
        })

        let pinnedItems = _.filter(gd.session.pins.models, (pin: Pin)=>{

            if (pin.companyId !== gd.companyId) return false
            if (pin.location.type !== PinLocationTypes.WORKSPACE || pin.location.value !== this.project.id) return false

            return !!pin.task || !!pin.document || !!pin.user || !!pin.project
        });

        return await new NavigationMenuSearch(keyword, this).search([this.project.id], pinnedItems, wsViews)
    }

    updateTitleAndIcon() {
        this.title  = this.project.name
        this.icon   = this.project.iconClass || this.project.projectIcon
    }
}
